import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
	Client,
	StrideDistrictReportData,
	StrideLogActivityFilter,
	StrideLogActivityListResult,
	StrideLogFilter,
	StrideLogReportData,
	StrideLogStudentSearchFilter,
	StrideLogStudentSearchResult,
	StrideSchoolReportData,
	StrideSchoolReportFilter,
	StrideSectionReportData,
	StrideSectionReportFilter,
	StrideStudentReportData,
	StrideStudentReportFilter,
	_,
} from '@mira-avant/mtlibs';
import { ClientService } from './client.service';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class StrideReports {
	private readonly BASE_PATH = 'stride-reports';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(
		private api: ApiService,
		private clientService: ClientService
	) {}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async getStrideDistrictReport$(productName:string): Promise<StrideDistrictReportData> {
		return this.withLoading(async () => {
			return firstValueFrom(
				this.api.post<StrideDistrictReportData>(`${this.BASE_PATH}/district-report/${productName}`, {})
			);
		});
	}

	public async getStrideSchoolReport$(
		productName:string,
		filter: StrideSchoolReportFilter
	): Promise<StrideSchoolReportData> {
		return this.withLoading(async () => {
			return firstValueFrom(
				this.api.post<StrideSchoolReportData>(`${this.BASE_PATH}/school-report/${productName}`, filter)
			);
		});
	}

	public async getStrideSectionReport$(sectionUuid: string): Promise<StrideSectionReportData> {
		return this.withLoading(async () => {
			const client: Client = await firstValueFrom(this.clientService.selectedClient$);

			const filter: StrideSectionReportFilter = {
				clientUuid: client.uuid,
				sectionUuid,
			};

			return firstValueFrom(
				this.api.post<StrideSectionReportData>(`${this.BASE_PATH}/section-report`, filter)
			);
		});
	}

	public async getStrideStudentReport$(
		studentUuid: string,
		sectionUuid: string
	): Promise<StrideStudentReportData> {
		return this.withLoading(async () => {
			const client: Client = await firstValueFrom(this.clientService.selectedClient$);

			const filter: StrideStudentReportFilter = {
				clientUuid: client.uuid,
				studentUuid,
				sectionUuid,
			};

			return firstValueFrom(
				this.api.post<StrideStudentReportData>(`${this.BASE_PATH}/student-report`, filter)
			);
		});
	}

	public async getStrideLogReport$(filter: Omit<StrideLogFilter, 'clientUuid'> & { clientUuid: string }): Promise<StrideLogReportData> {
		return this.withLoading(async () => {
			return firstValueFrom(this.api.post<StrideLogReportData>(`${this.BASE_PATH}/algorithm-report`, filter));
		});
	}

	// clientUuid must be supplied explicitly (from the admin's own client-selector control) rather
	// than from ClientService.selectedClient$, which is populated by the learner-facing section
	// selection flow and never emits on this admin-only page.
	public async getStrideLogStudents$(clientUuid: string, search?: string): Promise<StrideLogStudentSearchResult> {
		return this.withLoading(async () => {
			const filter: StrideLogStudentSearchFilter = {
				clientUuid,
				search,
			};

			return firstValueFrom(
				this.api.post<StrideLogStudentSearchResult>(`${this.BASE_PATH}/students`, filter)
			);
		});
	}

	public async getStrideLogActivities$(
		clientUuid: string,
		studentUuid: string,
		page: number = 1
	): Promise<StrideLogActivityListResult> {
		return this.withLoading(async () => {
			const filter: StrideLogActivityFilter = {
				clientUuid,
				studentUuid,
				page,
			};

			return firstValueFrom(
				this.api.post<StrideLogActivityListResult>(`${this.BASE_PATH}/activities`, filter)
			);
		});
	}
}
